import React, { useState, useMemo } from "react";
import {
  Header,
  Container,
  SpaceBetween,
  Box,
  Table,
  Pagination,
  TextFilter,
  Spinner,
  Select,
  SelectProps,
} from "@cloudscape-design/components";
import Plot from "react-plotly.js";
import { COLORS } from "./detailHelpers";
import { PAGE_SIZE_OPTIONS } from "../constants";
import type { ProgramDetailData, RequestStatus, DetailLineUtilizationRow, DetailLineUtilizationTrend, DetailLineUtilizationTrendDataPoint } from "../../../redux/types/programTypes";

export interface LineCapacityRow {
  week: string;
  firstDay: string;
  program: string;
  subProgram: string;
  cmLocation: string;
  uph: number;
  effMax: string;
  fpyGoal: string;
  lineApproved: number;
  workingHoursPerShift: number;
  shift: number;
  maxDRR: number;
  maxWRR7Days: number;
  weeklyInstalledCap: number;
  weeklyOutput: number;
  lineUtilization: string;
  cumInstalledCap: number;
  cumOutput: number;
  overallUtilization: string;
}

export const LineUtilizationDetail: React.FC<{ detailData?: ProgramDetailData | null; detailStatus?: RequestStatus }> = ({ detailData = null, detailStatus = "idle" }) => {
  const [tablePage, setTablePage] = useState(1);
  const [lineSearchText, setLineSearchText] = useState("");
  const [selectedPageSize, setSelectedPageSize] = useState<SelectProps.Option>(PAGE_SIZE_OPTIONS[0] as SelectProps.Option);
  const TABLE_PAGE_SIZE = parseInt(selectedPageSize.value || "10", 10);

  const apiTrend = detailData?.trend as DetailLineUtilizationTrend[] | undefined;

  const tableData = useMemo(() => {
    if (!detailData?.table) return [];
    return (detailData.table as DetailLineUtilizationRow[]).map((row) => ({
      week: row.week,
      firstDay: row.first_day || "",
      program: row.program,
      subProgram: row.parent_program || row.program,
      cmLocation: `${row.cm}-${row.cm_location}`,
      uph: row.uph,
      effMax: `${((row.eff_max ?? 0) * 100).toFixed(2)}%`,
      fpyGoal: `${((row.target ?? 0) * 100).toFixed(2)}%`,
      lineApproved: row.lines_approved,
      workingHoursPerShift: row.working_hours_per_shift,
      shift: row.shift,
      maxDRR: row.max_drr,
      maxWRR7Days: row.max_wrr7,
      weeklyInstalledCap: row.weekly_cap,
      weeklyOutput: row.weekly_output || 0,
      lineUtilization: `${((row.line_utilization ?? 0) * 100).toFixed(2)}%`,
      cumInstalledCap: row.cum_cap,
      cumOutput: row.cum_output,
      overallUtilization: `${((row.cum_utilization ?? 0) * 100).toFixed(2)}%`,
    }));
  }, [detailData]);

  const filteredLineData = useMemo(() => {
    if (!lineSearchText.trim()) return tableData;
    const q = lineSearchText.toLowerCase();
    return tableData.filter((r) => r.program.toLowerCase().includes(q) || r.cmLocation.toLowerCase().includes(q));
  }, [tableData, lineSearchText]);

  const pageItems = filteredLineData.slice((tablePage - 1) * TABLE_PAGE_SIZE, tablePage * TABLE_PAGE_SIZE);

  // Memoize chart data so pagination/search changes don't cause chart re-renders
  const chartData = useMemo(() => {
    if (!Array.isArray(apiTrend)) return [];
    const traces: any[] = [];
    let colorIdx = 0;
    apiTrend.forEach((item) => {
      if (!Array.isArray(item.data)) return;
      const xAxis = item.data.map((d: DetailLineUtilizationTrendDataPoint) => d.period);
      const y = item.data.map((d: DetailLineUtilizationTrendDataPoint) => d.line_utilization);
      traces.push({
        x: xAxis,
        y,
        name: `${item.program} (${item.cm}-${item.cm_location})`,
        type: "scatter" as const,
        mode: "lines+markers" as const,
        connectgaps: false,
        marker: { color: COLORS[colorIdx % COLORS.length], size: 5 },
        line: { width: 2, color: COLORS[colorIdx % COLORS.length] },
        hovertemplate: `<b>%{fullData.name}</b><br>Period: %{x}<br>Utilization: %{y:.2%}<br>Target: ${((item.target ?? 0) * 100).toFixed(2)}%<extra></extra>`,
      });
      colorIdx++;
    });
    return traces;
  }, [apiTrend]);

  const chartLayout = useMemo(() => ({
    height: 480, margin: { t: 30, b: 80, l: 50, r: 10 },
    showlegend: true,
    yaxis: { rangemode: "tozero" as const, title: "Line Utilization", tickformat: ".0%", domain: [0.3, 1] },
    xaxis: { title: "Period", tickangle: -45, type: "category" as const, automargin: true },
    legend: { orientation: "h" as const, y: 0, yanchor: "top" as const, x: 0.5, xanchor: "center" as const, font: { size: 10 } },
    font: { size: 10 },
  }), []);

  const chartConfig = useMemo(() => ({ displayModeBar: false, responsive: true }), []);

  if (detailStatus === "loading") {
    return (
      <Container header={<Header variant="h2">Line Capacity / Utilization — by Week</Header>}>
        <Box textAlign="center" padding={{ vertical: "xl" }}>
          <SpaceBetween size="s" alignItems="center">
            <Spinner size="large" />
            <Box color="text-body-secondary">Loading detail data...</Box>
          </SpaceBetween>
        </Box>
      </Container>
    );
  }

  return (
    <div data-testid="program-detail-lineutil">
    <Container header={<Header variant="h2"><span data-testid="program-detail-lineutil-title">Line Capacity / Utilization — by Week</span></Header>}>
      <SpaceBetween size="l">
        {/* Capacity Table */}
        <div className="prod-table-grouped detail-table-overflow" data-testid="program-table-lineutil">
        <Table
          filter={<div data-testid="program-table-lineutil-search"><TextFilter filteringText={lineSearchText} onChange={({ detail }) => { setLineSearchText(detail.filteringText); setTablePage(1); }} filteringPlaceholder="Search by program, CM or CM location..." /></div>}
          columnDefinitions={[
            { id: "program", header: "Program", cell: (item: LineCapacityRow) => {
              const idx = pageItems.indexOf(item);
              const programChanged = idx === 0 || pageItems[idx - 1]?.program !== item.program;
              return programChanged ? <span className="detail-program-cell">{item.program}</span> : "";
            }},
            { id: "week", header: "Week", cell: (item: LineCapacityRow) => item.week },
            { id: "firstDay", header: "First Day", cell: (item: LineCapacityRow) => item.firstDay },
            { id: "cmLocation", header: "CM & Location", cell: (item: LineCapacityRow) => {
              const idx = pageItems.indexOf(item);
              const programChanged = idx === 0 || pageItems[idx - 1]?.program !== item.program;
              const cmChanged = programChanged || pageItems[idx - 1]?.cmLocation !== item.cmLocation;
              return cmChanged ? <span className="detail-cm-cell">{item.cmLocation}</span> : "";
            }},
            { id: "uph", header: "UPH", cell: (item: LineCapacityRow) => item.uph.toLocaleString() },
            { id: "effMax", header: "Eff. Max", cell: (item: LineCapacityRow) => item.effMax },
            { id: "fpyGoal", header: "FPY Goal", cell: (item: LineCapacityRow) => item.fpyGoal },
            { id: "lineApproved", header: "Line Approved", cell: (item: LineCapacityRow) => item.lineApproved },
            { id: "workingHours", header: "Working Hours/Shift", cell: (item: LineCapacityRow) => item.workingHoursPerShift },
            { id: "shift", header: "Shift", cell: (item: LineCapacityRow) => item.shift },
            { id: "maxDRR", header: "Max DRR", cell: (item: LineCapacityRow) => item.maxDRR.toLocaleString() },
            { id: "maxWRR", header: "Max WRR/7 Days", cell: (item: LineCapacityRow) => item.maxWRR7Days.toLocaleString() },
            { id: "weeklyInstCap", header: "Weekly Installed Cap.", cell: (item: LineCapacityRow) => item.weeklyInstalledCap.toLocaleString() },
            { id: "weeklyOutput", header: "Weekly Output", cell: (item: LineCapacityRow) => item.weeklyOutput.toLocaleString() },
            { id: "lineUtil", header: "Line Utilization", cell: (item: LineCapacityRow) => item.lineUtilization },
            { id: "cumInstCap", header: "Cum Installed Cap", cell: (item: LineCapacityRow) => item.cumInstalledCap.toLocaleString() },
            { id: "cumOutput", header: "Cum Output", cell: (item: LineCapacityRow) => item.cumOutput.toLocaleString() },
            { id: "overallUtil", header: "Cum Line Utilization", cell: (item: LineCapacityRow) => item.overallUtilization },
          ]}
          items={pageItems}
          variant="embedded"
          empty={<Box textAlign="center" color="inherit" padding={{ vertical: "l" }}><b>No line utilization data</b><Box color="text-body-secondary" padding={{ top: "s" }}>No line capacity or utilization records available for the selected period.</Box></Box>}
          pagination={
            <div className="detail-table-toolbar">
              <div className="detail-page-size-selector">
                <span className="detail-page-size-label">Page size:</span>
                <div className="detail-page-size-select">
                  <Select
                    selectedOption={selectedPageSize}
                    onChange={({ detail }) => { setSelectedPageSize(detail.selectedOption as SelectProps.Option); setTablePage(1); }}
                    options={PAGE_SIZE_OPTIONS}
                  />
                </div>
              </div>
              {filteredLineData.length > TABLE_PAGE_SIZE && (
                <div data-testid="program-table-lineutil-pagination">
                  <Pagination currentPageIndex={tablePage} pagesCount={Math.ceil(filteredLineData.length / TABLE_PAGE_SIZE)} onChange={({ detail }) => setTablePage(detail.currentPageIndex)} />
                </div>
              )}
            </div>
          }
        />
        </div>

        {/* Separator */}
        <div className="detail-separator" />

        {/* Utilization Trend Chart - one line per program */}
        <div data-testid="program-chart-util-trend">
        <Box variant="h4" textAlign="center"><span data-testid="program-chart-util-trend-title">Utilization Trend Chart</span></Box>
        <div style={{ overflow: "visible", width: "100%" }}>
        <Plot
          data={chartData}
          layout={chartLayout}
          config={chartConfig}
          style={{ width: "100%", overflow: "visible" }}
        />
        </div>
        </div>
      </SpaceBetween>
    </Container>
    </div>
  );
};
